/**
 * Service de gestion des actions pour les devis
 * Centralise toutes les fonctionnalités liées aux boutons d'action du système comptable
 */

// Import des dépendances Firebase nécessaires
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  where,
  orderBy,
  getDocs
} from 'firebase/firestore';

import {
  creerDevis,
  creerFacture,
  accepterDevis,
  refuserDevis,
  STATUTS
} from './accountingService';
import { convertirProspectEnClient } from './unifiedClientService';
import { genererPDFDevis, imprimerDevis } from './devisPdfService';
import EmailService from './emailService';

/**
 * Service de gestion des actions pour les devis
 */
class DevisActionsService {
  constructor(db) {
    this.db = db;
  }

  /**
   * Récupère l'email actuel du client depuis sa fiche Firestore.
   * Si l'email a changé depuis la création du devis, synchronise le devis.
   * @param {Object} devis - Le devis contenant clientId et clientEmail
   * @returns {Promise<string>} L'email le plus récent du client
   */
  async getLatestClientEmail(devis) {
    if (!devis.clientId) return devis.clientEmail;

    try {
      const clientDoc = await getDoc(doc(this.db, 'clients', devis.clientId));
      if (clientDoc.exists()) {
        const clientData = clientDoc.data();
        if (clientData.email) {
          // Si l'email a changé, synchroniser le devis pour la prochaine fois
          if (clientData.email !== devis.clientEmail) {
            console.log(`🔄 Synchronisation email devis ${devis.numero}: ${devis.clientEmail} → ${clientData.email}`);
            try {
              await updateDoc(doc(this.db, 'devis', devis.id), {
                clientEmail: clientData.email
              });
            } catch (err) {
              console.warn('⚠️ Erreur mise à jour email devis:', err);
            }
          }
          return clientData.email;
        }
      }
    } catch (err) {
      console.error('❌ Erreur récupération email client actuel:', err);
    }
    return devis.clientEmail;
  }

  /**
   * Voir les détails d'un devis
   */
  async voirDetails(devis) {
    console.log('📄 Affichage des détails du devis:', devis.numero);
    return { success: true, devis };
  }

  /**
   * Modifier un devis
   */
  async modifierDevis(devis, modifications) {
    console.log('✏️ Modification du devis:', devis.numero);
    // Implémentation de la modification du devis
    return { success: true, devis: { ...devis, ...modifications } };
  }

  /**
   * Envoyer un devis au client par email
   * Récupère automatiquement l'email actuel du client depuis sa fiche
   */
  async envoyerDevis(devis, options = {}) {
    console.log('📧 Envoi du devis:', devis.numero);

    try {
      // Récupérer l'email actuel du client (pas le snapshot du devis)
      if (!options.destinataire) {
        const emailActuel = await this.getLatestClientEmail(devis);
        if (emailActuel) {
          options.destinataire = emailActuel;
        }
      }

      // Initialiser le service d'email
      const emailService = new EmailService(this.db);

      // Envoyer l'email avec le template professionnel
      const resultatEmail = await emailService.envoyerDevis(devis, options);

      // Mettre à jour le statut du devis
      const devisRef = doc(this.db, 'devis', devis.id);
      await updateDoc(devisRef, {
        statut: STATUTS.DEVIS.ENVOYE,
        dateEnvoi: new Date(),
        destinataireEmail: options.destinataire || devis.clientEmail,
        notesEnvoi: options.notes || '',
        emailMessageId: resultatEmail.messageId,
        emailStatus: resultatEmail.status
      });

      return {
        success: true,
        message: 'Devis envoyé avec succès',
        devis: { ...devis, statut: STATUTS.DEVIS.ENVOYE },
        emailResult: resultatEmail
      };
    } catch (error) {
      console.error('Erreur envoi devis:', error);
      throw error;
    }
  }

  /**
   * Accepter un devis et convertir le prospect en client
   */
  async accepterDevis(devis, options = {}) {
    console.log('✅ Acceptation du devis:', devis.numero);

    try {
      const resultat = await accepterDevis(this.db, devis.id, options);
      return resultat;
    } catch (error) {
      console.error('Erreur acceptation devis:', error);
      throw error;
    }
  }

  /**
   * Refuser un devis avec raison
   */
  async refuserDevis(devis, options = {}) {
    console.log('❌ Refus du devis:', devis.numero);

    try {
      const resultat = await refuserDevis(this.db, devis.id, options);
      return resultat;
    } catch (error) {
      console.error('Erreur refus devis:', error);
      throw error;
    }
  }

  /**
   * Créer une facture à partir d'un devis accepté
   */
  async creerFacture(devis, options = {}) {
    console.log('💰 Création de facture pour le devis:', devis.numero);

    try {
      if (devis.statut !== STATUTS.DEVIS.ACCEPTE) {
        throw new Error('Le devis doit être accepté pour créer une facture');
      }

      const facture = await creerFacture(this.db, devis.id, options);
      return { success: true, facture };
    } catch (error) {
      console.error('Erreur création facture:', error);
      throw error;
    }
  }

  /**
   * Dupliquer un devis
   */
  async dupliquerDevis(devis, options = {}) {
    console.log('📋 Duplication du devis:', devis.numero);

    try {
      // Préparer les données pour la création du nouveau devis
      const contrat = devis.contratParams || {
        contractType: 'mandat',
        weeklyHours: 10,
        baseRateType: 'standard'
      };

      const lignesPrestations = devis.lignesPrestations || [];

      const client = {
        id: devis.clientId,
        firstName: devis.clientNom?.split(' ')[0] || '',
        lastName: devis.clientNom?.split(' ').slice(1).join(' ') || '',
        email: devis.clientEmail || '',
        phone: devis.clientTelephone || '',
        address: devis.clientAdresse || ''
      };

      // Créer le nouveau devis
      const resultat = await creerDevis(
        this.db,
        contrat,
        lignesPrestations,
        client,
        {
          ...options,
          dateValidite: options.dateValidite || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 jours par défaut
          notes: options.notes || `Duplicata du devis ${devis.numero}`
        }
      );

      return {
        success: true,
        message: 'Devis dupliqué avec succès',
        nouveauDevis: resultat
      };
    } catch (error) {
      console.error('Erreur duplication devis:', error);
      throw error;
    }
  }

  /**
   * Télécharger le devis en PDF
   */
  async telechargerPDF(devis, options = {}) {
    console.log('📥 Téléchargement PDF du devis:', devis.numero);

    try {
      await genererPDFDevis(devis, options);
      return { success: true, message: 'PDF téléchargé avec succès' };
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      throw error;
    }
  }

  /**
   * Imprimer un devis
   */
  async imprimerDevis(devis, options = {}) {
    console.log('🖨️ Impression du devis:', devis.numero);

    try {
      await imprimerDevis(devis, options);
      return { success: true, message: 'Impression lancée' };
    } catch (error) {
      console.error('Erreur impression devis:', error);
      throw error;
    }
  }

  /**
   * Archiver un devis
   */
  async archiverDevis(devis, options = {}) {
    console.log('📁 Archivage du devis:', devis.numero);

    try {
      const devisRef = doc(this.db, 'devis', devis.id);
      await updateDoc(devisRef, {
        archive: true,
        dateArchivage: new Date(),
        raisonArchivage: options.raison || 'Archivage manuel'
      });

      return { success: true, message: 'Devis archivé avec succès' };
    } catch (error) {
      console.error('Erreur archivage devis:', error);
      throw error;
    }
  }

  /**
   * Supprimer un devis
   */
  async supprimerDevis(devis, options = {}) {
    console.log('🗑️ Suppression du devis:', devis.numero);

    try {
      // Vérifier si le devis peut être supprimé
      if (devis.statut === STATUTS.DEVIS.ACCEPTE && devis.factureId) {
        throw new Error('Impossible de supprimer un devis accepté avec facture associée');
      }

      const devisRef = doc(this.db, 'devis', devis.id);
      await deleteDoc(devisRef);

      return { success: true, message: 'Devis supprimé avec succès' };
    } catch (error) {
      console.error('Erreur suppression devis:', error);
      throw error;
    }
  }

  /**
   * Relancer un client pour un devis envoyé
   * Récupère automatiquement l'email actuel du client depuis sa fiche
   */
  async relancerClient(devis, options = {}) {
    console.log('🔔 Relance client pour le devis:', devis.numero);

    try {
      // Récupérer l'email actuel du client (pas le snapshot du devis)
      if (!options.destinataire) {
        const emailActuel = await this.getLatestClientEmail(devis);
        if (emailActuel) {
          options.destinataire = emailActuel;
        }
      }

      // Initialiser le service d'email
      const emailService = new EmailService(this.db);

      // Envoyer l'email de relance
      const resultatEmail = await emailService.envoyerRelance(devis, options);

      // Mettre à jour le compteur de relances
      const devisRef = doc(this.db, 'devis', devis.id);
      const relancesActuelles = devis.nombreRelances || 0;

      await updateDoc(devisRef, {
        nombreRelances: relancesActuelles + 1,
        dateDerniereRelance: new Date(),
        notesRelance: options.message || 'Relance standard',
        emailRelanceMessageId: resultatEmail.messageId,
        emailRelanceStatus: resultatEmail.status
      });

      return {
        success: true,
        message: 'Relance envoyée avec succès',
        emailResult: resultatEmail
      };
    } catch (error) {
      console.error('Erreur relance client:', error);
      throw error;
    }
  }

  /**
   * Réouvrir un devis refusé
   */
  async reouvrirDevis(devis, options = {}) {
    console.log('🔄 Réouverture du devis:', devis.numero);

    try {
      const devisRef = doc(this.db, 'devis', devis.id);
      await updateDoc(devisRef, {
        statut: STATUTS.DEVIS.BROUILLON,
        dateReouverture: new Date(),
        notesReouverture: options.notes || 'Réouverture manuelle'
      });

      return { success: true, message: 'Devis réouvert avec succès' };
    } catch (error) {
      console.error('Erreur réouverture devis:', error);
      throw error;
    }
  }

  /**
   * Renouveler un devis expiré
   */
  async renouvelerDevis(devis, options = {}) {
    console.log('🔄 Renouvellement du devis:', devis.numero);

    try {
      // Dupliquer le devis avec une nouvelle date de validité
      const nouveauDevis = await this.dupliquerDevis(devis, {
        ...options,
        dateValidite: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 jours
      });

      return {
        success: true,
        message: 'Devis renouvelé avec succès',
        nouveauDevis
      };
    } catch (error) {
      console.error('Erreur renouvellement devis:', error);
      throw error;
    }
  }

  /**
   * Partager un devis avec d'autres utilisateurs
   */
  async partagerDevis(devis, utilisateurs, options = {}) {
    console.log('🔗 Partage du devis:', devis.numero);

    try {
      // Créer des liens de partage ou notifications
      const liensPartage = utilisateurs.map(user => ({
        userId: user.id,
        datePartage: new Date(),
        permissions: options.permissions || ['lecture']
      }));

      const devisRef = doc(this.db, 'devis', devis.id);
      await updateDoc(devisRef, {
        partages: [...(devis.partages || []), ...liensPartage],
        dateDernierPartage: new Date()
      });

      return { success: true, message: 'Devis partagé avec succès' };
    } catch (error) {
      console.error('Erreur partage devis:', error);
      throw error;
    }
  }

  /**
   * Envoyer un rappel pour un devis envoyé
   */
  async envoyerRappel(devis, options = {}) {
    console.log('🔔 Envoi rappel pour le devis:', devis.numero);

    try {
      // Envoyer un email de rappel
      // await this.envoyerEmailRappel(devis, options);

      // Mettre à jour le compteur de rappels
      const devisRef = doc(this.db, 'devis', devis.id);
      const rappelsActuels = devis.nombreRappels || 0;

      await updateDoc(devisRef, {
        nombreRappels: rappelsActuels + 1,
        dateDernierRappel: new Date(),
        notesRappel: options.message || 'Rappel standard'
      });

      return { success: true, message: 'Rappel envoyé avec succès' };
    } catch (error) {
      console.error('Erreur envoi rappel:', error);
      throw error;
    }
  }

  /**
   * Contacter le client pour un devis
   */
  async contacterClient(devis, options = {}) {
    console.log('📞 Contact client pour le devis:', devis.numero);

    try {
      // Logique de contact client (appel, email, etc.)
      const devisRef = doc(this.db, 'devis', devis.id);
      await updateDoc(devisRef, {
        dateDernierContact: new Date(),
        notesContact: options.notes || 'Contact client',
        typeContact: options.type || 'général'
      });

      return { success: true, message: 'Client contacté avec succès' };
    } catch (error) {
      console.error('Erreur contact client:', error);
      throw error;
    }
  }

  /**
   * Ouvrir une négociation pour un devis
   */
  async negocierDevis(devis, options = {}) {
    console.log('🤝 Ouverture de négociation pour le devis:', devis.numero);

    try {
      // Mettre à jour le statut du devis en négociation
      const devisRef = doc(this.db, 'devis', devis.id);
      await updateDoc(devisRef, {
        statutNegociation: 'en_cours',
        dateDebutNegociation: new Date(),
        notesNegociation: options.notes || 'Négociation ouverte',
        montantPropose: options.montantPropose || devis.montantTTC,
        conditionsNegociees: options.conditions || devis.conditionsPaiement
      });

      // Mettre à jour le statut commercial du client/prospect
      const clientRef = doc(this.db, 'clients', devis.clientId);
      await updateDoc(clientRef, {
        statutCommercial: 'negociation',
        dateDerniereMiseAJour: new Date(),
        notesCommerciales: `Négociation ouverte pour le devis ${devis.numero}`
      });

      return {
        success: true,
        message: 'Négociation ouverte avec succès',
        devis: { ...devis, statutNegociation: 'en_cours' }
      };
    } catch (error) {
      console.error('Erreur ouverture négociation:', error);
      throw error;
    }
  }

  /**
   * Planifier une intervention pour un devis accepté
   */
  async planifierIntervention(devis, options = {}) {
    console.log('📅 Planification intervention pour le devis:', devis.numero);

    try {
      if (devis.statut !== STATUTS.DEVIS.ACCEPTE) {
        throw new Error('Le devis doit être accepté pour planifier une intervention');
      }

      // Créer une intervention dans la base de données
      const interventionData = {
        devisId: devis.id,
        clientId: devis.clientId,
        clientNom: devis.clientNom,
        datePlanification: options.date || new Date(),
        dureeEstimee: options.duree || 2, // heures par défaut
        notes: options.notes || '',
        statut: 'planifiee',
        createdBy: options.userId
      };

      const docRef = await addDoc(collection(this.db, 'interventions'), interventionData);

      return {
        success: true,
        message: 'Intervention planifiée avec succès',
        interventionId: docRef.id
      };
    } catch (error) {
      console.error('Erreur planification intervention:', error);
      throw error;
    }
  }

  /**
   * Créer un contrat à partir d'un devis accepté
   */
  async creerContrat(devis, options = {}) {
    console.log('📝 Création contrat pour le devis:', devis.numero);

    try {
      if (devis.statut !== STATUTS.DEVIS.ACCEPTE) {
        throw new Error('Le devis doit être accepté pour créer un contrat');
      }

      // Créer un contrat dans la base de données
      const contratData = {
        devisId: devis.id,
        clientId: devis.clientId,
        clientNom: devis.clientNom,
        dateDebut: options.dateDebut || new Date(),
        dateFin: options.dateFin || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 an par défaut
        conditions: options.conditions || devis.conditionsPaiement,
        statut: 'actif',
        createdBy: options.userId
      };

      const docRef = await addDoc(collection(this.db, 'contrats'), contratData);

      // Lier le contrat au devis
      const devisRef = doc(this.db, 'devis', devis.id);
      await updateDoc(devisRef, {
        contratId: docRef.id
      });

      return {
        success: true,
        message: 'Contrat créé avec succès',
        contratId: docRef.id
      };
    } catch (error) {
      console.error('Erreur création contrat:', error);
      throw error;
    }
  }

  /**
   * Générer un nouveau numéro de devis
   */
  async genererNouveauNumeroDevis() {
    try {
      const devisQuery = query(
        collection(this.db, 'devis'),
        orderBy('numero', 'desc'),
        where('numero', '>=', `DEV-${new Date().getFullYear()}-`)
      );
      const devisSnapshot = await getDocs(devisQuery);
      const dernierDevis = devisSnapshot.docs[0]?.data();
      const dernierNumero = dernierDevis ? parseInt(dernierDevis.numero.split('-')[2]) : 0;

      return `DEV-${new Date().getFullYear()}-${(dernierNumero + 1).toString().padStart(4, '0')}`;
    } catch (error) {
      console.error('Erreur génération numéro devis:', error);
      throw error;
    }
  }

  /**
   * Exécuter une action par son ID
   */
  async executerAction(actionId, devis, options = {}) {
    const actions = {
      'voir': this.voirDetails.bind(this),
      'modifier': this.modifierDevis.bind(this),
      'envoyer': this.envoyerDevis.bind(this),
      'accepter': this.accepterDevis.bind(this),
      'refuser': this.refuserDevis.bind(this),
      'negocier': this.negocierDevis.bind(this),
      'creer_facture': this.creerFacture.bind(this),
      'dupliquer': this.dupliquerDevis.bind(this),
      'telecharger': this.telechargerPDF.bind(this),
      'imprimer': this.imprimerDevis.bind(this),
      'archiver': this.archiverDevis.bind(this),
      'supprimer': this.supprimerDevis.bind(this),
      'relancer': this.relancerClient.bind(this),
      'reouvrir': this.reouvrirDevis.bind(this),
      'renouveler': this.renouvelerDevis.bind(this),
      'partager': this.partagerDevis.bind(this),
      'envoyer_rappel': this.envoyerRappel.bind(this),
      'contacter_client': this.contacterClient.bind(this),
      'planifier_intervention': this.planifierIntervention.bind(this),
      'creer_contrat': this.creerContrat.bind(this)
    };

    if (!actions[actionId]) {
      throw new Error(`Action non supportée: ${actionId}`);
    }

    return await actions[actionId](devis, options);
  }
}

export default DevisActionsService;
